/**
 * The Iron Triangle slot between the Source and Destination schema boxes
 * (ACTION_PLAN.md §7/§9's own "rendered in the portal per completed job"
 * item, closed here — E7 shipped the backend, this closes the portal
 * half).
 *
 * Three states, never a fabricated triangle in between:
 *   1. Neither schema dropped yet — a quiet placeholder.
 *   2. Both dropped, no job yet — a debounced call to
 *      `GET /generate/estimate` shows the one real pre-generation number
 *      (AI price quote vs. a hand-written-estimate baseline). Not the
 *      `<IronTriangle>` component itself: time and quality don't exist
 *      until a job actually runs, and passing fabricated zeros for them
 *      would always score a perfect TES regardless of the real cost —
 *      the degenerate two-zero-axis case `fibtec-iron-triangle`'s own
 *      spec (§4) names as a documented weakness. See
 *      `generate_estimate/handler.py`'s module docstring for the same
 *      reasoning on the backend.
 *   3. A job has completed — the one real, fully-populated triangle,
 *      via the shared `<IronTriangle>` component.
 */

import { useEffect, useState } from 'react';

import { IronTriangle } from '@fibtecltd/iron-triangle';

import { getGenerateEstimate, type GeneratedCode, type SchemaInput } from '../api/client';
import type { DroppedSchema } from './DragDropBoxes';

function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

function toSchemaInput(schema: DroppedSchema): SchemaInput {
  return { format: schema.format, content: schema.content };
}

const ESTIMATE_DEBOUNCE_MS = 500;

interface GenerationTriangleProps {
  source: DroppedSchema | null;
  destination: DroppedSchema | null;
  /** Set once a generation attempt has completed successfully — carries
   *  the one real triangle (`result.triangle`). */
  result: GeneratedCode | null;
  /** A generation attempt is in flight — dims the estimate rather than
   *  flickering it away, same `stale` convention `<IronTriangle>` itself
   *  uses for its own `actual` overlay. */
  busy: boolean;
}

export function GenerationTriangle({ source, destination, result, busy }: GenerationTriangleProps) {
  const ready = Boolean(source?.format && source.content && destination?.format && destination.content);

  const [estimate, setEstimate] = useState<{
    priceExpectedGbpPence: number;
    devCostGbpPence: number;
  } | null>(null);
  const [estimateError, setEstimateError] = useState(false);

  useEffect(() => {
    if (!ready || result) {
      setEstimate(null);
      setEstimateError(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      getGenerateEstimate(toSchemaInput(source!), toSchemaInput(destination!))
        .then((res) => {
          if (cancelled) return;
          setEstimate({
            priceExpectedGbpPence: res.price_expected_gbp_pence,
            devCostGbpPence: res.dev_cost_gbp_pence,
          });
          setEstimateError(false);
        })
        .catch(() => {
          // Fail quiet, same posture as this portal's other secondary-
          // info fetches: the drop boxes and the Generate button still
          // work regardless of whether this preview loads.
          if (!cancelled) setEstimateError(true);
        });
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // source/destination are plain objects recreated on every drop, so
    // depend on the two fields the estimate request actually sends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, result, source?.format, source?.content, destination?.format, destination?.content]);

  if (result) {
    const q = result.triangle.q;
    return (
      <div className="triangle-slot">
        <IronTriangle
          expected={result.triangle}
          quality={{
            axisLabel: 'Accuracy',
            basis: 'unresolved-field proxy',
            label: `Accuracy: ${Math.round((1 - q) * 100)}%`,
          }}
          stale={busy}
          size={220}
          // Native cost/time/quality are 0=ideal, 1=worst (spec's own
          // framing) — meaningful for the raw-axis audit table, but a
          // triangle whose "good" result reads as three zeros (a point
          // collapsed to the center) reads backwards to anyone not
          // steeped in the spec. §8's invert flips the *display* only
          // (never re-derives TES) to Savings/Speed/Accuracy, so 0 cost
          // reads as 100% savings — matches this component's own
          // Accuracy chip above, which was already computing 1-q.
          invert
        />
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="triangle-slot triangle-slot--empty">
        <p className="muted small">Drop both schemas to see a cost estimate here.</p>
      </div>
    );
  }

  if (estimateError) {
    return (
      <div className="triangle-slot triangle-slot--empty">
        <p className="muted small">Cost estimate unavailable right now.</p>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="triangle-slot triangle-slot--empty">
        <p className="muted small">Estimating…</p>
      </div>
    );
  }

  const ratio = estimate.devCostGbpPence
    ? estimate.priceExpectedGbpPence / estimate.devCostGbpPence
    : 0;

  return (
    <div className="triangle-slot">
      <p className="triangle-estimate-price">{formatPence(estimate.priceExpectedGbpPence)}</p>
      <p className="muted small">
        Estimated AI cost — vs. ~{formatPence(estimate.devCostGbpPence)} hand-written (
        {ratio < 1 ? `${Math.round((1 - ratio) * 100)}% cheaper` : `${ratio.toFixed(1)}×`})
      </p>
      <p className="muted small triangle-estimate-note">
        Time and accuracy appear here once generation completes.
      </p>
    </div>
  );
}
